import axios from 'axios';

// Création de l'instance axios avec l'URL de base
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://holyhubs.com/api',
  withCredentials: false, // Ne pas envoyer de cookies lors des requêtes cross-origin
  timeout: 10000 // Timeout de 10 secondes pour éviter les requêtes bloquées
});

// Log de l'URL de base pour débogage
console.log('API baseURL:', api.defaults.baseURL);

// Intercepteur pour ajouter le token d'authentification aux requêtes
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['x-auth-token'] = token;
    }
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs de réponse
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    if (error.response) {
      // Si on reçoit un 401 (non autorisé)
      if (error.response.status === 401) {
        // Nettoyer le token et rediriger vers la page de login
        localStorage.removeItem('token');
        localStorage.removeItem('admin');
        
        if (window.location.pathname.includes('/admin') && 
            !window.location.pathname.includes('/login')) {
          window.location.href = '/admin/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Services pour les scammers
export const scammerService = {
  getAll: async () => {
    try {
      const response = await api.get('/scammers');
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching scammers:', error);
      throw error;
    }
  },
  getById: async (id) => {
    try {
      const response = await api.get(`/scammers/${id}`);
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching scammer with ID ${id}:`, error);
      throw error;
    }
  },
  create: async (data) => {
    try {
      const response = await api.post('/scammers', data);
      return response.data;
    } catch (error) {
      console.error('Error creating scammer:', error);
      throw error;
    }
  },
  update: async (id, data) => {
    try {
      const response = await api.put(`/scammers/${id}`, data);
      return response.data;
    } catch (error) {
      console.error(`Error updating scammer with ID ${id}:`, error);
      throw error;
    }
  },
  delete: async (id) => {
    try {
      const response = await api.delete(`/scammers/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting scammer with ID ${id}:`, error);
      throw error;
    }
  },
};

// Services pour les tickets
export const ticketService = {
  getAll: () => api.get('/tickets'),
  getById: (id) => api.get(`/tickets/${id}`),
  getByStatus: (status) => api.get(`/tickets/status/${status}`),
  create: (data) => api.post('/tickets', data),
  updateStatus: (id, status, reason) => api.put(`/tickets/${id}/status`, { status, reason }),
  delete: (id) => api.delete(`/tickets/${id}`),
  uploadImages: (formData) => {
    return api.post('/tickets/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }
};

// Services pour les escrows
export const escrowService = {
  getAll: async (status = 'all') => {
    try {
      const response = await api.get(`/escrows${status !== 'all' ? `?status=${status}` : ''}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching escrows:', error);
      throw error;
    }
  },
  getById: async (id) => {
    try {
      const response = await api.get(`/escrows/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching escrow with ID ${id}:`, error);
      throw error;
    }
  },
  assignEscrow: async (id, data) => {
    try {
      const response = await api.put(`/escrows/${id}/assign`, data);
      return response.data;
    } catch (error) {
      console.error(`Error assigning escrow with ID ${id}:`, error);
      throw error;
    }
  },
  completeEscrow: async (id) => {
    try {
      const response = await api.put(`/escrows/${id}/complete`);
      return response.data;
    } catch (error) {
      console.error(`Error completing escrow with ID ${id}:`, error);
      throw error;
    }
  },
  rejectEscrow: async (id, reason) => {
    try {
      const response = await api.put(`/escrows/${id}/reject`, { reason });
      return response.data;
    } catch (error) {
      console.error(`Error rejecting escrow with ID ${id}:`, error);
      throw error;
    }
  },
  updateEscrow: async (id, data) => {
    try {
      const response = await api.put(`/escrows/${id}`, data);
      return response.data;
    } catch (error) {
      console.error(`Error updating escrow with ID ${id}:`, error);
      throw error;
    }
  },
  addLog: async (id, logMessage) => {
    try {
      const response = await api.put(`/escrows/${id}`, { log: logMessage });
      return response.data;
    } catch (error) {
      console.error(`Error adding log to escrow with ID ${id}:`, error);
      throw error;
    }
  },
  sendFileToBuyer: async (id, message) => {
    try {
      const response = await api.post(`/escrows/${id}/send-to-buyer`, { message });
      return response.data;
    } catch (error) {
      console.error(`Error sending file to buyer for escrow ID ${id}:`, error);
      throw error;
    }
  },
  sendContentToBuyer: async (id, message) => {
    try {
      const response = await api.post(`/escrows/${id}/send-content`, { message });
      return response.data;
    } catch (error) {
      console.error(`Error sending content to buyer for escrow ID ${id}:`, error);
      throw error;
    }
  },
  uploadFile: async (id, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post(`/escrows/${id}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error(`Error uploading file for escrow ID ${id}:`, error);
      throw error;
    }
  },
  uploadTextOrLink: async (id, content, isLink = false) => {
    try {
      const response = await api.put(`/escrows/${id}`, { 
        externalFileUrl: isLink ? content : null,
        textContent: !isLink ? content : null,
        log: `${isLink ? 'Lien' : 'Texte'} ajouté par l'administrateur`
      });
      
      return response.data;
    } catch (error) {
      console.error(`Error adding ${isLink ? 'link' : 'text'} to escrow ID ${id}:`, error);
      throw error;
    }
  }
};

// Services pour l'authentification
export const authService = {
  login: async (credentials) => {
    try {
      const response = await api.post('/auth/login', credentials);
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('admin', JSON.stringify(response.data.admin));
      }
      return response;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },
  register: (userData) => api.post('/auth/register', userData),
  getCurrentAdmin: () => api.get('/auth/admin'),
  updateProfile: (data) => api.put('/auth/admin/profile', data),
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('admin');
  },
  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },
};

export default api; 